import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Option, mergeClasses } from '@fluentui/react-components';
import type { ScheduleEvent, RoomTypeConfig } from '@/types/reports';
import { EventCard } from '../EventCard';
import { RunOfShowSummary } from '../RunOfShowSummary';
import { RoomTypeLegend } from '../RoomTypeLegend';
import { useRunOfShowTimelineStyles } from './RunOfShowTimeline.styles';

export interface RunOfShowTimelineProps {
  events: ScheduleEvent[];
  roomTypes: RoomTypeConfig[];
  largeEventThreshold: number;
}

export const RunOfShowTimeline = ({ events, roomTypes, largeEventThreshold }: RunOfShowTimelineProps) => {
  const styles = useRunOfShowTimelineStyles();
  const { t } = useTranslation('reports');

  const distinctDates = useMemo(() => {
    const dates = new Set(events.map((e) => e.startTime.split('T')[0] ?? ''));
    return [...dates].sort();
  }, [events]);

  const [selectedDate, setSelectedDate] = useState<string>(distinctDates[0] ?? '');
  const [selectedRoomType, setSelectedRoomType] = useState<string>('');

  const filteredEvents = useMemo(() => {
    let filtered = events.filter((e) => e.startTime.startsWith(selectedDate));
    if (selectedRoomType) {
      filtered = filtered.filter((e) => e.roomType === selectedRoomType);
    }
    return filtered.sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [events, selectedDate, selectedRoomType]);

  const summary = useMemo(() => ({
    totalEvents: filteredEvents.length,
    totalAttendees: filteredEvents.reduce((sum, e) => sum + e.guestCount, 0),
    largeEventCount: filteredEvents.filter((e) => e.guestCount >= largeEventThreshold).length,
  }), [filteredEvents, largeEventThreshold]);

  const roomColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    roomTypes.forEach((rt) => { map[rt.name] = rt.color; });
    return map;
  }, [roomTypes]);

  const formatDate = (iso: string) => {
    const date = new Date(iso + 'T00:00:00');
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'long', day: 'numeric' }).format(date);
  };

  return (
    <div className={styles.root} data-testid="run-of-show-timeline">
      <div className={mergeClasses(styles.toolbar, styles.toolbarResponsive)}>
        <div className={styles.filterRow}>
          <Dropdown
            className={styles.pillDropdown}
            aria-label={t('runOfShow.dateLabel')}
            value={selectedDate ? formatDate(selectedDate) : ''}
            onOptionSelect={(_, data) => setSelectedDate(data.optionValue as string)}
          >
            {distinctDates.map((d) => (
              <Option key={d} value={d}>{formatDate(d)}</Option>
            ))}
          </Dropdown>
          <Dropdown
            className={styles.pillDropdown}
            aria-label={t('runOfShow.roomTypeLabel')}
            value={selectedRoomType || t('runOfShow.allTypes')}
            onOptionSelect={(_, data) => setSelectedRoomType(data.optionValue === '__all__' ? '' : (data.optionValue as string))}
          >
            <Option value="__all__">{t('runOfShow.allTypes')}</Option>
            {roomTypes.map((rt) => (
              <Option key={rt.name} value={rt.name}>{rt.name}</Option>
            ))}
          </Dropdown>
        </div>
        <RunOfShowSummary
          totalEvents={summary.totalEvents}
          totalAttendees={summary.totalAttendees}
          largeEventCount={summary.largeEventCount}
        />
      </div>

      <RoomTypeLegend
        roomTypes={roomTypes}
        largeEventThreshold={largeEventThreshold}
        largeEventLabel={t('runOfShow.largeThreshold', { threshold: largeEventThreshold })}
      />

      <div className={styles.eventList} role="list" aria-label={t('runOfShow.eventList')}>
        {filteredEvents.length === 0 ? (
          <div className={styles.emptyState}>{t('runOfShow.noEvents')}</div>
        ) : (
          filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              roomColor={roomColorMap[event.roomType] ?? '#888'}
              largeEventThreshold={largeEventThreshold}
            />
          ))
        )}
      </div>
    </div>
  );
};

RunOfShowTimeline.displayName = 'RunOfShowTimeline';
